<template>
    <div ref="container" :class="['cz__image-editor', { 'cz__image-loading': false }]">
        <canvas ref="canvas"></canvas>

        <portal to="editor-overlays" :order="1">
            <div v-show="editPlacement && (rotateable || sizeable)" ref="sizeguide" :class="[ 'cz__image-sizeguide', 'cz__image-interact-inner' ]">
                <div v-show="tooltipVisible && moveable" ref="moveguide" class="cz__image-move-guide">
                    <span class="cz__image-moveguide-tooltip">
                        <span>Drag to move</span>
                    </span>
                </div>
                <button ref="sizeguidehandle">
                    <transition name="fade-sizeguide-tooltip">
                        <span v-show="tooltipVisible" class="cz__image-sizeguide-tooltip">
                            <template v-if="rotateable">
                                <span v-if="sizeable">Drag to resize and rotate</span>
                                <span v-else>Drag to rotate</span>
                            </template>
                            <template v-else>
                                <span v-if="sizeable">Drag to resize</span>
                            </template>
                        </span>
                    </transition>
                </button>
            </div>
        </portal>
    </div>
</template>

<style lang="less">
    .cz__image-editor {
        position: absolute;

        left: 0;
        top: 0;

        width: 100%;
        height: 100%;

        overflow: hidden;

        canvas {
            cursor: move;
        }
    }

    .cz__image-sizeguide {
        position: absolute;

        left: 50%;
        top: 50%;

        width: 200px;
        height: 200px;

        transform: translate(-50%, -50%);

        border-radius: 300px;

        border: 1px solid white;
        box-shadow: 0px 0px 2px #000, 0px 0px 2px #000 inset;

        pointer-events: none;

        &:before {
            content: '';

            position: absolute;

            left: 50%;
            top: 50%;

            width: 8px;
            height: 8px;

            border-radius: 8px;

            transform: translate(-50%, -50%);

            border: 1px solid white;
            box-shadow: 0px 0px 2px #000, 0px 0px 2px #000 inset;
        }

        .cz__image-move-guide {
            position: absolute;

            right: 50%;
            top: 50%;

            width: 1px;
            height: 1px;
        }

        .cz__image-moveguide-tooltip {
            position: absolute;

            right: 0;
            top: 0;

            transform: translate(20px, 15px);

            padding: 5px 10px;

            border-radius: 2px;

            background: #000;
            color: #fff;

            font-size: 10px;

            text-transform: uppercase;

            white-space: nowrap;

            &:after {
                content: '';

                position: absolute;

                top: -5px;
                right: 15px;

                width: 0;
                height: 0;
                border-left: 5px solid transparent;
                border-right: 5px solid transparent;

                border-bottom: 5px solid #000;
            }
        }

        button {
            position: absolute;

            width: 20px;
            height: 20px;

            border-radius: 20px;

            margin-left: -10px;
            margin-top: -10px;

            left: 50%;
            top: 50%;

            transform: translate(100px, 0);

            background: #fff;

            border: none;
            box-shadow: 0px 0px 2px rgba(0, 0, 0, 1);

            pointer-events: auto;
            cursor: move;

            &:focus {
                outline: none;
            }

            .cz__image-sizeguide-tooltip {
                position: absolute;

                left: -10px;
                bottom: 30px;

                padding: 5px 10px;

                border-radius: 2px;

                background: #000;
                color: #fff;

                font-size: 10px;

                text-transform: uppercase;

                white-space: nowrap;

                &:after {
                    content: '';

                    position: absolute;

                    bottom: -5px;
                    left: 15px;

                    width: 0;
                    height: 0;
                    border-left: 5px solid transparent;
                    border-right: 5px solid transparent;

                    border-top: 5px solid #000;
                }
            }
        }
    }

    .fade-sizeguide-tooltip-enter-active, .fade-sizeguide-tooltip-leave-active {
        transition: opacity .25s;
    }

    .fade-sizeguide-tooltip-enter, .fade-sizeguide-tooltip-leave-to {
        opacity: 0;
    }

</style>

<script>
    import interact from 'interactjs';

    const GUIDE_BASE_RADIUS = 100;

    export default {
        props: {
            controller: Object,
            view: Object,
        },
        data() {
            return {
                editPlacement: null,

                progressX: null,
                progressY: null,
                progressScale: null,
                progressRotate: null,

                moveable: false,
                sizeable: false,
                rotateable: false,

                tooltipVisible: true,
            };
        },
        watch: {
            controller: {
                handler(newValue, oldValue) {
                    // Rebind controller events.
                    if (oldValue) {
                        Object.keys(this.controllerEvents).forEach((e) => {
                            oldValue.off(e, this.controllerEvents[e]);
                        });
                    }

                    Object.keys(this.controllerEvents).forEach((e) => {
                        newValue.on(e, this.controllerEvents[e]);
                    });

                    this.createImage();
                },
                deep: false,
            },

            view() {
                this.selectView();
            },
        },
        beforeCreate() {
            this.controllerEvents = {
                stateChanged: e => this.onStateChanged(e),
                repaint: e => this.onRepaint(e),
            };
        },
        destroy() {
            // Stop listening to controller events.
            if (this.controller) {
                Object.keys(this.controllerEvents).forEach((e) => {
                    this.controller.off(e, this.controllerEvents[e]);
                });
            }
        },
        mounted() {
            this.createImage();

            this.$nextTick(() => {
                this.enableInteract();
            });

            window.addEventListener('resize', () => this.resize());
        },
        updated() {
            this.$nextTick(() => {
                this.enableInteract();
            });
        },
        beforeUpdate() {
            this.removeInteract();
        },
        methods: {
            // Provide a proxy to the API object and listen to important events.
            createImage() {
                this.resize();

                if (this.controller) {
                    this.selectView();
                }
            },

            /**
             * Selects the current view.
             */
            selectView() {
                this.editPlacement = this.view && this.view.custom && this.view.custom['editor-placement'];

                const editPlacement = this.controller.state.find(p => p.placement.code === this.editPlacement);
                this.placement = editPlacement;

                // eslint-disable-next-line
                if (this.placement) {
                    this.loadImage();
                }

                // Enable interactions if the component was rendered already.
                if (this.$refs.sizeguide) {
                    this.enableInteract();
                }
            },

            /**
             * Resize editor.
             */
            resize() {
                this.$nextTick(() => {
                    const { canvas, container } = this.$refs;

                    const width = container.clientWidth;
                    const height = container.clientHeight;

                    canvas.width = width * 2;
                    canvas.height = height * 2;
                    canvas.style.width = width + 'px';
                    canvas.style.height = height + 'px';

                    this.paint();
                });
            },

            /**
             * Loads and caches the image to be edited.
             */
            loadImage() {
                if (this.placement) {
                    const image = this.placement.component.custom && this.placement.component.custom['dynamic-image-jpgUrl'];

                    if (image) {
                        this.image = this.makeLoader(image);

                        const mask = this.view && this.view.custom && this.view.custom['editor-mask'];

                        if (mask) {
                            const maskUrl =
                                this.controller.baseFrameUrl() +
                                'src=' +
                                this.controller.mapImageName(
                                    this.controller.imageName(mask, null, this.view, this.view.frameLow),
                                    this.controller.blueprint.imageMapping,
                                    {
                                        background: true,
                                    }
                                );

                            this.mask = this.makeLoader(maskUrl);
                        } else {
                            this.mask = null;
                        }
                    } else {
                        this.image = null;
                        this.mask = null;

                        this.paint();
                    }
                }
            },

            /**
             * Creates image loader.
             */
            makeLoader(url) {
                const self = this;

                const loader = {
                    url,
                    ready: false,
                    error: false,
                    image: new Image(),
                    xhr: new XMLHttpRequest(),
                };

                loader.xhr.onload = function onXhrLoad() {
                    const blobUrl = URL.createObjectURL(this.response);

                    loader.image.onload = function onImageLoad() {
                        loader.ready = true;
                        URL.revokeObjectURL(blobUrl);

                        self.paint();
                    };

                    loader.image.src = blobUrl;
                };

                loader.xhr.onerror = function onXhrError() {
                    if (!loader.error) {
                        loader.ready = true;
                        loader.error = true;

                        self.paint();
                    }
                };

                loader.xhr.onreadystatechange = function onReadyState(e) {
                    if (e && e.target) {
                        if (e.target.status > 200) {
                            loader.ready = true;
                            loader.error = true;

                            self.paint();
                        }
                    }
                };

                window.requestAnimationFrame(() => {
                    loader.xhr.open('GET', loader.url, true);
                    loader.xhr.responseType = 'blob';
                    loader.xhr.send();
                });

                return loader;
            },

            /**
             * Paint the image in the editor.
             * @return {void}
             */
            paint() {
                // Bail if prerequisites are not loaded yet.
                if (!this.placement) {
                    return;
                }

                if (!this.image || !this.image.ready) {
                    return;
                }

                if (this.mask && !this.mask.ready) {
                    return;
                }

                const surface = this.$refs.canvas;

                if (!surface) {
                    return;
                }

                // Clear canvas before rendering new content.
                const w = surface.width;
                const h = surface.height;

                const ctx = surface.getContext('2d');
                ctx.clearRect(0, 0, w, h);

                const dpiScaleFactor = this.backingScale();

                if (this.image.error) {
                    return;
                }

                if (this.mask.error) {
                    return;
                }

                const { component } = this.placement;

                // Extact dynamic image placement.
                let x = +((component.custom && component.custom['dynamic-image-x']) || '0');
                let y = +((component.custom && component.custom['dynamic-image-y']) || '0');

                const offsetX = +((component.custom && component.custom['dynamic-image-offset-x']) || '0');
                const offsetY = +((component.custom && component.custom['dynamic-image-offset-y']) || '0');

                x += offsetX;
                y += offsetY;

                let scale = +((component.custom && component.custom['dynamic-image-scale']) || '1');
                let rotate = +((component.custom && component.custom['dynamic-image-rotate']) || '0');

                // Apply temporary adjustments.
                if (this.progressX != null) {
                    x += this.progressX;
                }

                if (this.progressY != null) {
                    y += this.progressY;
                }

                if (this.progressScale != null) {
                    scale = this.progressScale;
                }

                if (this.progressRotate != null) {
                    rotate = this.progressRotate;
                }

                // We could use either live image dimensions or ones stored as dynamic-image-width/height.
                const iw = this.image.image.width;
                const ih = this.image.image.height;

                // Figure out base image scaling factor to fit into the allowed space.
                const { baseScale, dw, dh } = this.baseScale();

                const diw = iw * scale;
                const dih = ih * scale;

                const ix = (x - (diw / 2)) * dpiScaleFactor * baseScale;
                const iy = (y - (dih / 2)) * dpiScaleFactor * baseScale;

                // eslint-disable-next-line
                const cx = (((w - dw) / 2) + ix) | 0;
                // eslint-disable-next-line
                const cy = (((h - dh) / 2) + iy) | 0;

                const targetWidth = diw * dpiScaleFactor * baseScale;
                const targetHeight = dih * dpiScaleFactor * baseScale;

                // Draw image rotated around center.
                ctx.translate(cx + (targetWidth / 2), cy + (targetHeight / 2));
                ctx.rotate((rotate * Math.PI) / 180);

                ctx.drawImage(this.image.image, 0, 0, iw, ih, -targetWidth / 2, -targetHeight / 2, targetWidth, targetHeight);

                ctx.resetTransform();

                if (this.mask) {
                    ctx.rotate(0);

                    ctx.globalCompositeOperation = 'destination-in';

                    // Mask is supposed to be of the same dimensions as the base image.
                    // eslint-disable-next-line
                    ctx.drawImage(this.mask.image, 0, 0, this.mask.image.width, this.mask.image.height, ((w - dw) / 2) | 0, ((h - dh) / 2) | 0, dw, dh);

                    ctx.globalCompositeOperation = 'source-over';
                }

                // Place guides, which are real DOM content and are using CSS instead of being painted on the canvas.
                const guideSize = GUIDE_BASE_RADIUS * scale;

                this.$refs.sizeguide.style.width = Math.floor(guideSize * 2) + 'px';
                this.$refs.sizeguide.style.height = Math.floor(guideSize * 2) + 'px';

                this.$refs.sizeguide.style.transform =
                    'translate(calc(-50% + ' + ((offsetX + (this.progressX || 0)) * baseScale) +
                    'px), calc(-50% + ' + ((offsetY + (this.progressY || 0)) * baseScale) +
                    'px)) rotate(' + rotate + 'deg)';

                this.$refs.sizeguidehandle.style.transform = 'translate(' + Math.floor(guideSize) + 'px, 0) rotate(' + (-rotate) + 'deg)';
                this.$refs.moveguide.style.transform = 'rotate(' + (-rotate) + 'deg)';
            },

            /**
             * Calculates canvas backing scale for HI-DPI screens.
             * @return {Number} backing scale.
             */
            backingScale() {
                return 2;
            },

            /**
             * Calculates a scaling factor to fit the base image onto the canvas.
             * @return {Number}
             */
            baseScale() {
                // Base width and height are used to determine overall scaling factor to fit images into the allowed screen space.
                const baseWidth = this.view.custom && this.view.custom['editor-base-width'] ? +this.view.custom['editor-base-width'] : 1;
                const baseHeight = this.view.custom && this.view.custom['editor-base-height'] ? +this.view.custom['editor-base-height'] : 1;

                const surface = this.$refs.canvas;

                const w = surface.width;
                const h = surface.height;

                const dpiScaleFactor = this.backingScale();

                // Figure out base image scaling factor to fit into the allowed space.
                let dw = baseWidth * dpiScaleFactor;
                let dh = baseHeight * dpiScaleFactor;

                let baseScale = 1;

                if (dw > w) {
                    baseScale = w / dw;
                    // eslint-disable-next-line
                    dh = (dh * baseScale) | 0;
                    dw = w;
                }

                if (dh > h) {
                    baseScale = h / dh;
                    // eslint-disable-next-line
                    dw = (dw * baseScale) | 0;
                    dh = h;
                }

                return {
                    baseScale,
                    dw,
                    dh,
                };
            },

            /**
             * Removes InteractJS integration, typically before VueJS DOM updates.
             * @return {void}
             */
            removeInteract() {
                if (this.interact) {
                    interact(this.$refs.container).unset();
                    interact(this.$refs.sizeguidehandle).unset();

                    this.interact = false;
                }
            },

            /**
             * Enables user interaction with the editor.
             */
            enableInteract() {
                this.removeInteract();

                if (!this.placement) {
                    return;
                }

                const { component } = this.placement;

                // eslint-disable-next-line
                let mainInteractable = interact(this.$refs.container);

                // eslint-disable-next-line
                let sizeGuide = interact(this.$refs.sizeguidehandle);

                // Allow to drag the image on the surface.
                this.moveable = component.custom && component.custom['dynamic-image-allow-move'] === 'yes';
                this.sizeable = component.custom && component.custom['dynamic-image-allow-resize'];
                this.rotateable = component.custom && component.custom['dynamic-image-allow-rotate'];

                if (this.moveable) {
                    mainInteractable = mainInteractable.draggable({
                        ignoreFrom: '.cz__image-interact-inner',

                        onstart: () => {
                            if (this.placement) {
                                this.progressX = 0;
                                this.progressY = 0;
                            }
                        },

                        onend: () => {
                            if (this.placement) {
                                if (this.progressX || this.progressY) {
                                    // Commit changes.
                                    const x = +((component.custom && component.custom['dynamic-image-offset-x']) || '0') + (this.progressX || 0);
                                    const y = +((component.custom && component.custom['dynamic-image-offset-y']) || '0') + (this.progressY || 0);

                                    this.progressX = null;
                                    this.progressY = null;

                                    this.controller.commitCustomAttributes(this.placement.placement.code, {
                                        'dynamic-image-offset-x': x,
                                        'dynamic-image-offset-y': y,
                                    });
                                }
                            }
                        },

                        onmove: (event) => {
                            if (this.placement) {
                                const dx = event.pageX - event.x0;
                                const dy = event.pageY - event.y0;

                                // Use current viewport scale for 1:1 movement.
                                // Dynamic scale is not used for positioning.
                                const { baseScale } = this.baseScale();

                                const idx = dx / baseScale;
                                const idy = dy / baseScale;

                                this.progressX = idx;
                                this.progressY = idy;

                                this.paint();
                            }
                        },
                    });
                }

                if (this.sizeable || this.rotateable) {
                    sizeGuide = sizeGuide.draggable({
                        onstart: (event) => {
                            if (this.placement) {
                                const { scale, rotate } = this.getScaleRotation(event.pageX - event.x0, event.pageY - event.y0);

                                this.progressScale = scale;
                                this.progressRotate = rotate;

                                const { x: thumbX, y: thumbY } = this.getCenter(this.$refs.sizeguidehandle);

                                this.thumbX = thumbX;
                                this.thumbY = thumbY;
                            }
                        },

                        onend: () => {
                            if (this.placement) {
                                if (this.progressScale || this.progressRotate) {
                                    // Commit changes.
                                    const scale = this.progressScale;
                                    const rotate = this.progressRotate;

                                    this.progressScale = null;
                                    this.progressRotate = null;

                                    this.controller.commitCustomAttributes(this.placement.placement.code, {
                                        'dynamic-image-scale': scale,
                                        'dynamic-image-rotate': rotate,
                                    });
                                }
                            }
                        },

                        onmove: (event) => {
                            if (this.placement) {
                                const { scale, rotate } = this.getScaleRotation(event.pageX - event.x0, event.pageY - event.y0);

                                if (this.sizeable) {
                                    this.progressScale = scale;
                                }

                                if (this.rotateable) {
                                    this.progressRotate = rotate;
                                }

                                this.paint();
                            }
                        },
                    });
                }

                this.interact = true;
            },

            /**
             * Gets document-relative coordinates of the element's center, post all transforms.
             */
            getCenter(el) {
                const rect = el.getBoundingClientRect();

                const x = window.scrollX + rect.left + (rect.width * 0.5);
                const y = window.scrollY + rect.top + (rect.height * 0.5);

                return {
                    x,
                    y,
                };
            },

            /**
             * Calcuates scale and rotation based on the difference from the gizmo center.
             */
            getScaleRotation(offsetX, offsetY) {
                const { x: centerX, y: centerY } = this.getCenter(this.$refs.sizeguide);

                const dx = (this.thumbX + offsetX) - centerX;
                const dy = (this.thumbY + offsetY) - centerY;

                const r = Math.sqrt((dx * dx) + (dy * dy));

                let scale = r / GUIDE_BASE_RADIUS;

                const scaleMin = +(this.placement.component.custom['dynamic-image-scale-min'] || '0.1');
                const scaleMax = +(this.placement.component.custom['dynamic-image-scale-max'] || '5');

                scale = Math.min(scaleMax, Math.max(scaleMin, scale));

                const ndx = dx / r;
                const ndy = dy / r;

                const rotate = (Math.atan2(ndy, ndx) * 180) / Math.PI;

                return {
                    scale,
                    rotate,
                };
            },

            /**
             * Called whenever controller state change happens.
             * @return {void}
             */
            onStateChanged() {
                if (this.placement) {
                    // Reload dynamic image of the placement had changed.
                    const image = this.placement.component.custom && this.placement.component.custom['dynamic-image-jpgUrl'];

                    if (!this.image || this.image.url !== image) {
                        this.selectView();
                    }
                }

                this.paint();
            },

            /**
             * Called whenever controller requests explicit repaint.
             * @return {void}
             */
            onRepaint() {
                this.paint();
            },
        },
    };
</script>